import withErrorBoundary from 'Common/ErrorBoundary/withErrorBoundary';
import { ChannelExpressObject } from 'Features/VideoPlayer/Entities/channel-express-object';
import { JoinChannelOptions } from 'Features/VideoPlayer/Entities/joined-channel-options';
import React, { createContext, FC, PropsWithChildren, useContext, useRef, useState } from 'react';

import { JoinedChannel } from '../Entities/joined-channel.entity';
import { StreamStatus } from '../Enums/stream-status.enum';
import useIvsMediaStream from '../Hooks/useIvsMediaStream.hook';
import usePhenixMediaStream from '../Hooks/usePhenixMediaStream.hook';

const VideoPlayerProvider: FC<PropsWithChildren> = ({ children }) => {
    const joinedChannels = useRef<JoinedChannel[]>([]);
    const ivsMediaStreamProvider = useIvsMediaStream();
    const phenixMediaStreamProvider = usePhenixMediaStream();
    let [channelExpressObjects,] = useState<ChannelExpressObject[]>([]);
    const ivsLanesString = process.env.REACT_APP_IVS_LANES;
    const ivsLanes = ivsLanesString?.split(/\s*,\s*/);

    const getMediaStreamProvider = (alias: string) => {
        if (ivsLanes?.includes(alias)) {
            return ivsMediaStreamProvider;
        }
        else {
            return phenixMediaStreamProvider;
        }
    };

    const joinChannel = (joinChannelOptions: JoinChannelOptions) => {

        try {
            const { streamExistsForAlias, assignStreamToVideoElementByAlias } = getMediaStreamProvider(joinChannelOptions.alias);
            const channels = joinedChannels.current.filter((c: JoinedChannel) => c.alias === joinChannelOptions.alias);

            if (streamExistsForAlias(joinChannelOptions.alias)) {
                const joinedChannel = channels.find((c) => c.videoElement?.id === joinChannelOptions.videoElement.id);

                if (!joinedChannel || !joinedChannel.videoElement) {
                    const newJoinedChannel = {
                        ...channels[0],
                        alias: joinChannelOptions.alias,
                        videoId: joinChannelOptions.videoElement.id,
                        videoElement: joinChannelOptions.videoElement,
                        refNum: joinChannelOptions.refNum
                    } as JoinedChannel;

                    assignStreamToVideoElementByAlias(joinChannelOptions);
                    const index = joinedChannels.current.findIndex(x => x.refNum === newJoinedChannel.refNum);

                    if (index < 0)
                        joinedChannels.current.push(newJoinedChannel);
                    else {
                        joinedChannels.current.splice(joinedChannels.current.findIndex(x => x.refNum === newJoinedChannel.refNum), 1);
                        joinedChannels.current.push(newJoinedChannel);
                    }
                }
            } else {
                addChannel(joinChannelOptions);
            }
        } catch (error) {
            window.logger.error('VideoProvider-join-channel', error);
        }
    };

    const addChannel = (joinChannelOptions: JoinChannelOptions) => {

        const newJoinedChannel = {
            alias: joinChannelOptions.alias,
            videoElement: joinChannelOptions.videoElement,
            disposables: [],
            refNum: joinChannelOptions.refNum
        } as JoinedChannel;

        if (channelExpressObjects.findIndex(x => x.joinChannelOptions.alias === newJoinedChannel.alias) < 0) {

            if (channelExpressObjects.findIndex(x => x.refNum === joinChannelOptions.refNum) > -1) {
                const alias = channelExpressObjects.filter(x => x.refNum === joinChannelOptions.refNum)[0].joinChannelOptions.alias;

                //Below code is to handle producer stream change from block
                if (alias !== joinChannelOptions.alias) {
                    const expressObjectIndex = channelExpressObjects.findIndex(x => x.refNum === joinChannelOptions.refNum);
                    const channelIndex = joinedChannels.current.findIndex(x => x.refNum === joinChannelOptions.refNum);

                    if (channelIndex > -1)
                        joinedChannels.current.splice(channelIndex, 1);

                    if (expressObjectIndex > -1)
                        channelExpressObjects.splice(expressObjectIndex, 1);

                    addChannel(joinChannelOptions);

                    if (joinedChannels.current.findIndex(x => x.alias === alias) > -1) {

                        const videoElement: HTMLVideoElement = joinedChannels.current.filter(x => x.alias === alias)[0]?.videoElement;
                        const refNum = joinedChannels.current.filter(x => x.alias === alias)[0]?.refNum;

                        const newJoinChannelOptions: JoinChannelOptions = {
                            videoElement: videoElement,
                            refNum: refNum,
                            alias: alias,
                            streamSelectionStrategy: 'high-availability'
                        }

                        addChannel(newJoinChannelOptions);
                    }
                }
            }
            else {
                const { connectToStream } = getMediaStreamProvider(joinChannelOptions.alias);

                connectToStream(newJoinedChannel, joinChannelOptions)
                    .then(raiseNotPlayingEvent);
            }
        }

        const index = joinedChannels.current.findIndex(x => x.refNum === newJoinedChannel.refNum);

        if (index < 0)
            joinedChannels.current.push(newJoinedChannel);
        else {
            joinedChannels.current.splice(joinedChannels.current.findIndex(x => x.refNum === newJoinedChannel.refNum), 1);
            joinedChannels.current.push(newJoinedChannel);
        }
        if (channelExpressObjects.findIndex(x => x.joinChannelOptions.alias === newJoinedChannel.alias) < 0)
            channelExpressObjects.push({ refNum: joinChannelOptions.refNum, joinChannelOptions: joinChannelOptions })

    };

    const raiseNotPlayingEvent = (joinedChannel: JoinedChannel) => {
        joinedChannels.current
            .filter((c) => c.alias === joinedChannel.alias && c.videoElement?.id !== joinedChannel.videoElement?.id)
            .forEach((c) => {
                c.videoElement?.dispatchEvent(new CustomEvent('channel_status', { bubbles: true, detail: StreamStatus.NOT_PLAYING }));
            });
    }

    const removeMediaStream = (alias: string) => {
        const { forgetMediaStream } = getMediaStreamProvider(alias);

        joinedChannels.current
            .filter((x) => x.alias === alias)
            .forEach((c) => c.videoElement?.dispatchEvent(new CustomEvent('channel_status', { detail: StreamStatus.NO_STREAM_PLAYING })));

        forgetMediaStream(alias);
    };

    const disposeChannelExpressObjectOnStreamRejoin = (joinedChannel: JoinChannelOptions) => {
        const refNum: string = joinedChannel.refNum;

        if (channelExpressObjects.length > 0 && (channelExpressObjects.findIndex(x => x.refNum === refNum) > -1)) {
            //if stream stop and start again then dispose current object and re-initialize same object with joincall method

            const videoElement: HTMLVideoElement = joinedChannels.current.filter(x => x.refNum === refNum)[0]?.videoElement;
            const expressObjectIndex = channelExpressObjects.findIndex(x => x.refNum === refNum);

            if (document.getElementById(videoElement.id)) {
                const joinChannelOptions: JoinChannelOptions = channelExpressObjects.filter((s: ChannelExpressObject) => s.refNum === refNum)[0]?.joinChannelOptions;

                if (expressObjectIndex > -1)
                    channelExpressObjects.splice(expressObjectIndex, 1);

                if (videoElement) {
                    joinChannelOptions.videoElement = videoElement;
                    addChannel(joinChannelOptions);
                }
            }
            else {
                //This code is for tablet when secondary sale is minimised

                if (expressObjectIndex > -1)
                    channelExpressObjects.splice(expressObjectIndex, 1);

                const channelIndex = joinedChannels.current.findIndex(x => x.refNum === refNum);

                if (channelIndex > -1)
                    joinedChannels.current.splice(channelIndex, 1);

                createExpressObjectForOtherChannel(joinedChannel);
            }

        }
        else {
            createExpressObjectForOtherChannel(joinedChannel);
        }
    }

    const createExpressObjectForOtherChannel = (joinedChannel: JoinChannelOptions,) => {
        const alias: string = joinedChannel.alias;

        //If default object is already disposed and still same alias channels are present then
        //new express object is created for remaining sales of same alias
        if (joinedChannels.current.findIndex(x => x.alias === alias) > -1) {
            const channel: JoinedChannel = joinedChannels.current.filter(x => x.alias === alias)[0];
            const videoElement: HTMLVideoElement = channel?.videoElement;
            const newRefNum: string = channel?.refNum;

            if (videoElement) {
                joinedChannel.videoElement = videoElement;
                joinedChannel.refNum = newRefNum;
                addChannel(joinedChannel);
            }
        }
    }

    const disposeChannelExpressObject = (refNum?: string | undefined) => {

        //On sale close remove channel from joinedChannels array and remove object from channelExpressObjects array
        if (refNum) {
            const channelIndex = joinedChannels.current.findIndex(x => x.refNum === refNum);
            const expressObjectIndex = channelExpressObjects.findIndex(x => x.refNum === refNum);

            if (channelIndex > -1)
                joinedChannels.current.splice(channelIndex, 1);

            const alias = channelExpressObjects.filter((s: ChannelExpressObject) => s.refNum === refNum)[0]?.joinChannelOptions.alias;

            if (joinedChannels.current.findIndex(x => x.alias === alias) > -1) {
                //If channels are present but without default object then create new object for same alias and default object is disposed and removed from array

                const joinChannelOptions: JoinChannelOptions = channelExpressObjects.filter((s: ChannelExpressObject) => s.refNum === refNum)[0]?.joinChannelOptions;

                if (expressObjectIndex > -1)
                    channelExpressObjects.splice(expressObjectIndex, 1);

                const videoElement: HTMLVideoElement = joinedChannels.current.filter(x => x.alias === alias)[0]?.videoElement;
                const newRefNum = joinedChannels.current.filter(x => x.alias === alias)[0]?.refNum;

                joinChannelOptions.videoElement = videoElement;
                joinChannelOptions.refNum = newRefNum;
                joinedChannels.current.filter(x => x.alias === alias).forEach(x => x.videoElement.dispatchEvent(new CustomEvent('channel_status', { bubbles: true, detail: StreamStatus.NO_STREAM_PLAYING })));
                addChannel(joinChannelOptions);
            }
            else {
                //If  channels are not present for same alias then default object is disposed and removed from array
                channelExpressObjects.filter((s: ChannelExpressObject) => s.refNum === refNum).forEach((x: ChannelExpressObject) => x.joinChannelOptions.videoElement.dispatchEvent(new CustomEvent('ended', { bubbles: true, detail: StreamStatus.STREAM_ENDED })));

                if (expressObjectIndex > -1)
                    channelExpressObjects.splice(expressObjectIndex, 1);

            }
        }
        else {
            joinedChannels.current = [];
            channelExpressObjects = [];
        }
    }

    return (
        <VideoPlayerContext.Provider
            value={{
                joinedChannels: joinedChannels.current,
                joinChannel,
                removeMediaStream,
                disposeChannelExpressObject
            }}>
            {children}
        </VideoPlayerContext.Provider>
    );
};

export interface IVideoPlayerContext {
    joinedChannels: JoinedChannel[];
    joinChannel: (joinChannelOptions: JoinChannelOptions) => void;
    removeMediaStream: (alias: string) => void;
    disposeChannelExpressObject: (refnum?: string | undefined) => void;
}

export const VideoPlayerContext = createContext<IVideoPlayerContext>({
    joinedChannels: [],
    joinChannel: (joinChannelOptions: JoinChannelOptions) => {
        return;
    },
    removeMediaStream: (alias: string) => {
        return;
    },
    disposeChannelExpressObject: (refNum?: string | undefined) => {
        return;
    }
});

export const useVideoPlayerContext = (): IVideoPlayerContext => useContext(VideoPlayerContext);

export default withErrorBoundary(VideoPlayerProvider);